/**
 * Expense reconciliation (owner, 19 Oct 2026: "in brightautozone section
 * create expense reconcialation section in which surbhi will upload expense
 * sheet and bank statement and app will match the sheets and sort expenses in
 * heads and detail analysis of all the expense and tracking"; "bank statemnet
 * is in excel and expenses are in google sheet"; "Heads from our sheet + app
 * suggests"; "the staff will upload the both files in the app directly").
 *
 * Two kinds of upload: the bank statement (every line of money in and out of
 * one account) and the office's expense sheet (every expense, its head, how it
 * was paid). The app pairs each sheet line paid from the bank with its
 * withdrawal; what is left on either side is the work — a sheet line the bank
 * never paid, a withdrawal nobody wrote down. Every withdrawal ends up in a
 * head, or marked "not an expense" (a supplier's payment, a transfer between
 * the company's own accounts). Amounts on the wire are rupees.
 */
import { isBookHead } from './book-wall.js';
import { HEAD_ADMIN_DESK_EMAILS } from './domain.js';

export const RECON_KINDS = ['BANK', 'EXPENSES'] as const;
export type ReconKind = (typeof RECON_KINDS)[number];
export const RECON_KIND_LABELS: Record<ReconKind, string> = { BANK: 'Bank statement', EXPENSES: 'Expense sheet' };

/** The columns read from a bank statement. Date and either debit/credit or amount are needed. */
export const RECON_BANK_FIELDS = ['date', 'description', 'ref', 'debit', 'credit', 'amount', 'drcr', 'balance'] as const;
export type ReconBankField = (typeof RECON_BANK_FIELDS)[number];
export const RECON_BANK_FIELD_LABELS: Record<ReconBankField, string> = {
  date: 'Date',
  description: 'Narration',
  ref: 'Cheque / ref no.',
  debit: 'Withdrawal (money out)',
  credit: 'Deposit (money in)',
  amount: 'Amount (one column)',
  drcr: 'Dr / Cr',
  balance: 'Balance',
};

/** The columns read from the expense sheet. Date and amount are needed. */
export const RECON_EXPENSE_FIELDS = ['date', 'amount', 'head', 'description', 'paidTo', 'mode', 'paidBy'] as const;
export type ReconExpenseField = (typeof RECON_EXPENSE_FIELDS)[number];
export const RECON_EXPENSE_FIELD_LABELS: Record<ReconExpenseField, string> = {
  date: 'Date',
  amount: 'Amount',
  head: 'Head',
  description: 'Details',
  paidTo: 'Paid to',
  mode: 'Paid how (cash / UPI / bank)',
  paidBy: 'Paid by',
};

export type ReconMapping = Partial<Record<ReconBankField | ReconExpenseField, number | null>>;

export const RECON_PAY_MODES = ['CASH', 'BANK', 'UPI', 'CARD', 'CHEQUE'] as const;
export type ReconPayMode = (typeof RECON_PAY_MODES)[number];
export const RECON_PAY_MODE_LABELS: Record<ReconPayMode, string> = { CASH: 'Cash', BANK: 'Bank', UPI: 'UPI', CARD: 'Card', CHEQUE: 'Cheque' };

/** A withdrawal's standing. */
export const RECON_BANK_STATES = ['MATCHED', 'SORTED', 'UNSORTED', 'NOT_EXPENSE'] as const;
export type ReconBankState = (typeof RECON_BANK_STATES)[number];
export const RECON_BANK_STATE_LABELS: Record<ReconBankState, string> = {
  MATCHED: 'Matched to the sheet',
  SORTED: 'Put in a head',
  UNSORTED: 'Not in the sheet',
  NOT_EXPENSE: 'Not an expense',
};

/** A sheet line's standing. Cash never reaches the bank, so it is not "missing". */
export const RECON_EXPENSE_STATES = ['MATCHED', 'CASH', 'MISSING'] as const;
export type ReconExpenseState = (typeof RECON_EXPENSE_STATES)[number];
export const RECON_EXPENSE_STATE_LABELS: Record<ReconExpenseState, string> = {
  MATCHED: 'Found in the bank',
  CASH: 'Paid in cash',
  MISSING: 'Not found in the bank',
};

/** The head every unsorted withdrawal sits under until somebody sorts it. */
export const RECON_UNSORTED = 'Not sorted yet';

/** Who reconciles: the owner's head-admin desks, and anyone switched on for it in Users (Surbhi). */
export function reconcilesExpenses(user: { email: string; organizationId?: string; reconcilesExpenses?: boolean | null } | null | undefined): boolean {
  if (!user) return false;
  if (HEAD_ADMIN_DESK_EMAILS.includes(user.email.trim().toLowerCase()) || user.reconcilesExpenses === true) return true;
  // A walled book's head keeps its own day book and bank statement (FAB EV, 29 Oct 2026).
  return user.organizationId !== undefined && isBookHead({ email: user.email, organizationId: user.organizationId });
}

export interface ReconPreview {
  kind: ReconKind;
  name: string;
  sheetName: string;
  headers: string[];
  /** The row of the file the headings were found on; null when none were. */
  headerRow: number | null;
  mapping: ReconMapping;
  /** Columns still to be chosen before it can be brought in. */
  missing: string[];
  counts: { rows: number; ready: number; duplicates: number; skipped: number };
  /**
   * The same Google Sheet is already in: bringing it in reads it again in place —
   * lines edited or deleted in the sheet since leave (removed), new ones come in.
   */
  refresh: { uploadId: string; removed: number } | null;
  period: { from: string | null; to: string | null };
  sample: Array<{ row: number; date: string; amount: number; direction: 'OUT' | 'IN'; text: string; head: string | null; mode: ReconPayMode | null }>;
  skipped: Array<{ row: number; reason: string }>;
}

export interface ReconUploadRow {
  id: string;
  kind: ReconKind;
  name: string;
  /** The Google Sheet it was read from, when it was a link — it can be read again. */
  link: string | null;
  account: string | null;
  uploadedBy: string | null;
  createdAt: string;
  withdrawnAt: string | null;
  /** A Google Sheet upload: when it was last read again. */
  refreshedAt: string | null;
  rows: number;
  added: number;
  duplicates: number;
  skipped: number;
  from: string | null;
  to: string | null;
}

export interface ReconImportResult {
  upload: ReconUploadRow;
  /** Pairs the matching found across everything after this upload. */
  matched: number;
  /** What changed: lines brought in, already in from another upload, and (a sheet read again) no longer in the sheet. */
  changes: { added: number; duplicates: number; removed: number };
}

/** Sorting a withdrawal into a head that the sheet seems to have already — the same amount within a month. */
export interface ReconSortClash {
  bankId: string;
  lines: Array<{ id: string; date: string; amount: number; head: string | null; text: string }>;
}

export interface ReconHeadTotal {
  head: string;
  amount: number;
  count: number;
  /** Of which: written in the sheet, and found only in the bank. */
  fromSheet: number;
  fromBankOnly: number;
  /** Share of everything spent in the period, 0–100. */
  share: number;
}

export interface ReconSummary {
  period: { from: string | null; to: string | null };
  /** What the bank and the sheet hold for the period. */
  totals: {
    /** Everything spent: every sheet line plus every withdrawal sorted into a head that the sheet never had. */
    spent: number;
    sheet: { count: number; amount: number };
    cash: { count: number; amount: number };
    bankOut: { count: number; amount: number };
    bankIn: { count: number; amount: number };
    matched: { count: number; amount: number };
    /** Sheet lines paid from the bank that the bank does not show. */
    missingFromBank: { count: number; amount: number };
    /** Withdrawals nobody wrote down and nobody has sorted yet. */
    unsorted: { count: number; amount: number };
    notExpense: { count: number; amount: number };
  };
  byHead: ReconHeadTotal[];
  /** Month by month, each head's spend: "YYYY-MM". */
  byMonth: Array<{ month: string; amount: number; heads: Record<string, number> }>;
  topPayees: Array<{ name: string; amount: number; count: number }>;
  heads: string[];
  /** Nothing uploaded yet of this kind. */
  empty: { bank: boolean; sheet: boolean };
}

export interface ReconSuggestion {
  head: string;
  basis: 'learned' | 'keyword';
  confidence: number;
}

export interface ReconBankRow {
  id: string;
  date: string;
  description: string;
  ref: string | null;
  /** Money out or money in; one of them is null. */
  out: number | null;
  in: number | null;
  balance: number | null;
  account: string | null;
  state: ReconBankState | null;
  head: string | null;
  suggestion: ReconSuggestion | null;
  note: string | null;
  match: { expenseIds: string[]; by: 'AUTO' | 'MANUAL'; score: number | null; lines: Array<{ id: string; date: string; amount: number; head: string | null; text: string }> } | null;
  upload: { id: string; name: string };
}

export interface ReconExpenseRow {
  id: string;
  date: string;
  amount: number;
  head: string | null;
  description: string | null;
  paidTo: string | null;
  mode: ReconPayMode | null;
  modeText: string | null;
  paidBy: string | null;
  state: ReconExpenseState;
  bank: { id: string; date: string; description: string; amount: number; by: 'AUTO' | 'MANUAL'; score: number | null } | null;
  upload: { id: string; name: string };
}

export interface ReconPage<T> {
  data: T[];
  meta: { page: number; pageSize: number; total: number; totalPages: number; counts: Record<string, number> };
}
